/**
 * SEMU Product List Component
 * A professional product listing with tabs and slider functionality
 * Version: 2.0
 * @module SEMUProductList
 */

(function () {
  'use strict';

  /**
   * Product List Component Class
   * @class SEMUProductList
   */
  class SEMUProductList {
    constructor(element) {
      this.container = element;
      this.tabs = element.querySelectorAll('.semu-product-list__tab-button');
      this.panels = element.querySelectorAll('.semu-product-list__panel');
      this.sliders = new Map();
      this.initialized = false;

      // Bind methods
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeyboard = this.handleTabKeyboard.bind(this);

      this.init();
    }

    /**
     * Initialize component
     */
    init() {
      if (this.initialized) return;

      try {
        // Initialize tabs if multiple blocks exist
        if (this.tabs.length > 1) {
          this.initTabs();
        }

        // Initialize visible sliders
        this.initVisibleSliders();

        this.initialized = true;
      } catch (error) {
        console.error('SEMUProductList initialization failed:', error);
      }
    }

    /**
     * Initialize tab functionality
     */
    initTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('keydown', this.handleTabKeyboard);
      });
    }

    /**
     * Handle tab click event
     * @param {Event} event
     */
    handleTabClick(event) {
      event.preventDefault();
      const tab = event.currentTarget;
      this.switchTab(tab);
    }

    /**
     * Handle tab keyboard navigation
     * @param {KeyboardEvent} event
     */
    handleTabKeyboard(event) {
      const tab = event.currentTarget;
      const tabs = Array.from(this.tabs);
      const currentIndex = tabs.indexOf(tab);
      let nextIndex;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) nextIndex = tabs.length - 1;
          tabs[nextIndex].focus();
          this.switchTab(tabs[nextIndex]);
          break;

        case 'ArrowRight':
          event.preventDefault();
          nextIndex = currentIndex + 1;
          if (nextIndex >= tabs.length) nextIndex = 0;
          tabs[nextIndex].focus();
          this.switchTab(tabs[nextIndex]);
          break;

        case 'Home':
          event.preventDefault();
          tabs[0].focus();
          this.switchTab(tabs[0]);
          break;

        case 'End':
          event.preventDefault();
          tabs[tabs.length - 1].focus();
          this.switchTab(tabs[tabs.length - 1]);
          break;
      }
    }

    /**
     * Switch to selected tab
     * @param {HTMLElement} selectedTab
     */
    switchTab(selectedTab) {
      const targetPanelId = selectedTab.getAttribute('data-tab-target');
      const targetPanel = document.getElementById(targetPanelId);

      if (!targetPanel) return;

      try {
        // Update tab states
        this.tabs.forEach((tab) => {
          const isActive = tab === selectedTab;
          tab.classList.toggle('semu-product-list__tab-button--active', isActive);
          tab.setAttribute('aria-selected', isActive.toString());
        });

        // Update panel states
        this.panels.forEach((panel) => {
          const isTarget = panel === targetPanel;
          panel.classList.toggle('semu-product-list__panel--hidden', !isTarget);
          panel.toggleAttribute('hidden', !isTarget);

          // Update inline style for extra safety
          panel.style.display = isTarget ? '' : 'none';

          // Initialize slider when panel becomes visible
          if (isTarget && panel.querySelector('.glide')) {
            this.initSliderInPanel(panel);
          }
        });
      } catch (error) {
        console.error('Tab switching failed:', error);
      }
    }

    /**
     * Initialize sliders in visible panels
     */
    initVisibleSliders() {
      this.panels.forEach((panel) => {
        if (!panel.classList.contains('semu-product-list__panel--hidden')) {
          this.initSliderInPanel(panel);
        }
      });
    }

    /**
     * Initialize slider in specific panel
     * @param {HTMLElement} panel
     */
    initSliderInPanel(panel) {
      const sliderEl = panel.querySelector('.glide');
      if (!sliderEl || !window.Glide) return;

      const sliderId = sliderEl.id;
      if (!sliderId) return;

      // Check if already initialized
      if (this.sliders.has(sliderId)) {
        const existingSlider = this.sliders.get(sliderId);
        // Verify slider is still in DOM
        if (document.getElementById(sliderId)) {
          return;
        } else {
          // Clean up orphaned slider
          this.destroySlider(sliderId);
        }
      }

      try {
        // Get slider options
        const options = this.getSliderOptions();

        // Create and mount slider
        const slider = new window.Glide(`#${sliderId}`, options);

        // Add event listeners
        slider.on('mount.after', () => {
          sliderEl.classList.add('glide--mounted');
        });

        slider.mount();

        // Store instance
        this.sliders.set(sliderId, slider);
      } catch (error) {
        console.error(`Failed to initialize slider ${sliderId}:`, error);
      }
    }

    /**
     * Get slider options from global store
     * @returns {Object} Glide options
     */
    getSliderOptions() {
      // Get section ID
      const section = this.container.closest('.shopify-section');
      const sectionId = section ? section.id.replace('shopify-section-', '') : null;

      // Return stored options or defaults
      if (sectionId && window.semuGlideOptions && window.semuGlideOptions[sectionId]) {
        return window.semuGlideOptions[sectionId];
      }

      // Default options
      return {
        type: 'carousel',
        startAt: 0,
        perView: 4,
        gap: 20,
        peek: 0,
        rewind: true,
        breakpoints: {
          990: { perView: 3 },
          750: { perView: 2 },
          490: { perView: 2 },
        },
      };
    }

    /**
     * Destroy specific slider
     * @param {string} sliderId
     */
    destroySlider(sliderId) {
      const slider = this.sliders.get(sliderId);
      if (slider && typeof slider.destroy === 'function') {
        try {
          slider.destroy();
        } catch (error) {
          console.error(`Error destroying slider ${sliderId}:`, error);
        }
      }
      this.sliders.delete(sliderId);
    }

    /**
     * Refresh all sliders
     */
    refresh() {
      // Destroy all sliders
      this.sliders.forEach((slider, sliderId) => {
        this.destroySlider(sliderId);
      });

      // Re-initialize visible sliders
      this.initVisibleSliders();
    }

    /**
     * Destroy component
     */
    destroy() {
      // Remove event listeners
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener('keydown', this.handleTabKeyboard);
      });

      // Destroy all sliders
      this.sliders.forEach((slider, sliderId) => {
        this.destroySlider(sliderId);
      });

      this.initialized = false;
    }
  }

  // Instance management using WeakMap for memory efficiency
  const instances = new WeakMap();

  /**
   * Initialize component instance
   * @param {HTMLElement} element
   */
  function initializeComponent(element) {
    if (!element) return;

    // Clean up existing instance
    if (instances.has(element)) {
      const instance = instances.get(element);
      instance.destroy();
    }

    // Create new instance
    const instance = new SEMUProductList(element);
    instances.set(element, instance);
  }

  /**
   * Initialize all components on page
   */
  function initializeAll() {
    const components = document.querySelectorAll('.semu-product-list');
    components.forEach(initializeComponent);
  }

  /**
   * Handle DOM ready state
   */
  function handleDOMReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeAll);
    } else {
      initializeAll();
    }
  }

  // Initialize on DOM ready
  handleDOMReady();

  // Shopify theme editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const productList = event.target.querySelector('.semu-product-list');
      if (productList) {
        initializeComponent(productList);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const productList = event.target.querySelector('.semu-product-list');
      if (productList && instances.has(productList)) {
        const instance = instances.get(productList);
        instance.destroy();
        instances.delete(productList);
      }
    });

    document.addEventListener('shopify:section:select', (event) => {
      const productList = event.target.querySelector('.semu-product-list');
      if (productList && instances.has(productList)) {
        const instance = instances.get(productList);
        instance.refresh();
      }
    });
  }

  // Export for debugging
  window.SEMUProductList = SEMUProductList;
})();
